/**
 * Simulation mode state management.
 * Simple module-level state with event emission for cross-component reactivity.
 * History is persisted to localStorage. Saved (pinned) entries survive the rolling limit.
 */

import type { SimulationResultDto } from '@/types/core/Simulation';

export interface SimulationHistoryEntry {
  result: SimulationResultDto;
  timestamp: number;
  label: string;
  saved?: boolean;
}

const MAX_UNSAVED = 20;
const HISTORY_STORAGE_KEY = 'nexo-simulation-history';

// --- localStorage helpers ---

function loadHistory(): SimulationHistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function persistHistory(history: SimulationHistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Keep all saved entries + trim unsaved to MAX_UNSAVED (newest first). */
function trimHistory(history: SimulationHistoryEntry[]): SimulationHistoryEntry[] {
  const saved = history.filter((e) => e.saved);
  const unsaved = history.filter((e) => !e.saved);
  return [...saved, ...unsaved.slice(0, MAX_UNSAVED)].sort((a, b) => b.timestamp - a.timestamp);
}

// --- State ---
let _simulationMode = false;
let _includeDraftRules = false;
let _lastResult: SimulationResultDto | null = null;
let _history: SimulationHistoryEntry[] = loadHistory();
const _listeners = new Set<() => void>();

export interface SimulationSnapshot {
  active: boolean;
  includeDrafts: boolean;
  lastResult: SimulationResultDto | null;
  history: SimulationHistoryEntry[];
}

// Cached snapshot — only replaced when state changes (required by useSyncExternalStore)
let _snapshot: SimulationSnapshot = { active: _simulationMode, includeDrafts: _includeDraftRules, lastResult: _lastResult, history: _history };

// --- Public API ---

export function isSimulationActive(): boolean {
  return _simulationMode;
}

export function isDraftRulesIncluded(): boolean {
  return _includeDraftRules;
}

export function getLastResult(): SimulationResultDto | null {
  return _lastResult;
}

export function enableSimulation(includeDrafts = false): void {
  _simulationMode = true;
  _includeDraftRules = includeDrafts;
  _lastResult = null;
  _notify();
}

export function disableSimulation(): void {
  _simulationMode = false;
  _includeDraftRules = false;
  _lastResult = null;
  _notify();
}

export function setIncludeDraftRules(include: boolean): void {
  _includeDraftRules = include;
  _notify();
}

export function setLastResult(result: SimulationResultDto | null): void {
  _lastResult = result;
  if (result) {
    const label = `${result.requestMethod} ${result.requestPath}`;
    _history = trimHistory([{ result, timestamp: Date.now(), label }, ..._history]);
    persistHistory(_history);
  }
  _notify();
}

export function toggleSaveEntry(timestamp: number): void {
  _history = _history.map((e) =>
    e.timestamp === timestamp ? { ...e, saved: !e.saved } : e,
  );
  _history = trimHistory(_history);
  persistHistory(_history);
  _notify();
}

export function clearHistory(): void {
  _history = [];
  persistHistory(_history);
  _notify();
}

// --- Subscription for React components ---

export function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Snapshot for useSyncExternalStore — returns a stable reference until state changes */
export function getSnapshot() {
  return _snapshot;
}

function _notify(): void {
  _snapshot = { active: _simulationMode, includeDrafts: _includeDraftRules, lastResult: _lastResult, history: _history };
  for (const listener of _listeners) {
    listener();
  }
}
